import { MapPin, Phone, Mail, Facebook, Linkedin, Instagram, Heart } from "lucide-react";
import logoImage from "@/assets/ez-logo.jpeg";

const navLinks = [
  { label: "About Us", href: "#about" },
  { label: "Product Range", href: "#products" },
  { label: "Our Promise", href: "#promise" },
  { label: "Services", href: "#services" },
  { label: "Location", href: "#location" },
  { label: "Request a Refill", href: "#refill" },
];

const services = [
  "Prescription Pickup",
  "Free Home Delivery",
  "Language Assistance",
  "Medicine Refill Reminders",
  "Quick Visit Options",
];

export default function Footer() {
  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer id="contact" data-testid="footer-section" className="bg-ez-teal-dark text-white">
      <div className="ez-container py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img
                src={logoImage}
                alt="EZ Care Pharmacy logo"
                className="w-16 h-16 object-contain"
              />
              <span className="text-xl font-bold font-outfit">EZ Care Pharmacy</span>
            </div>
            <p className="text-teal-200 text-sm leading-relaxed mb-5 font-jakarta">
              A women-owned, independent community pharmacy serving Essex County, New Jersey with genuine
              medicines, personalized care, and a welcoming environment.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                data-testid="social-facebook"
                aria-label="Facebook"
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href="#"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
              >
                <Linkedin size={16} />
              </a>
              <a
                href="#"
                data-testid="social-instagram"
                aria-label="Instagram"
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
              >
                <Instagram size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-bold text-base mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-teal-200 hover:text-white text-sm font-jakarta transition-colors hover:translate-x-1 inline-block"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-outfit font-bold text-base mb-4">Our Services</h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s} className="text-teal-200 text-sm font-jakarta">{s}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-bold text-base mb-4">Contact</h4>
            <div className="space-y-4">
              <a
                href="https://maps.google.com/?q=447+William+St+East+Orange+NJ"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address"
                className="flex items-start gap-3 group"
              >
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <MapPin size={14} />
                </div>
                <span className="text-teal-200 text-sm font-jakarta group-hover:text-white transition-colors">
                  447 William St,<br />East Orange, NJ 07017
                </span>
              </a>

              <a
                href="tel:+18625201063"
                data-testid="footer-phone"
                className="flex items-center gap-3 group"
              >
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <Phone size={14} />
                </div>
                <span className="text-teal-200 text-sm font-jakarta group-hover:text-white transition-colors">
                  (862) 520-1063
                </span>
              </a>

              <a
                href="mailto:ezcarepharmacynj@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-3 group"
              >
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <Mail size={14} />
                </div>
                <span className="text-teal-200 text-sm font-jakarta group-hover:text-white transition-colors">
                  ezcarepharmacynj@gmail.com
                </span>
              </a>
            </div>

            {/* Notice */}
            <div className="mt-6 p-4 bg-white/10 rounded-xl">
              <p className="text-teal-200 text-xs font-jakarta leading-relaxed">
                <strong className="text-white">Important Notice:</strong> This website does not collect, store, or process
                any personal or medical information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 py-5">
        <div className="ez-container flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-teal-300 text-xs font-jakarta">
            © {new Date().getFullYear()} EZ Care Pharmacy. All Rights Reserved.
          </p>
          <p className="text-teal-300 text-xs font-jakarta flex items-center gap-1">
            Made with <Heart size={12} className="text-red-400 fill-red-400" /> for our community
          </p>
        </div>
      </div>
    </footer>
  );
}
